import { useEffect, useMemo, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  defaultShipmentSkuReviewShowsAll,
  shipmentSkuReviewCounts,
  visibleShipmentSkuReviewItems,
  type ShipmentSkuReviewItem,
} from './ShipmentSkuReviewList'

const REVIEW_ROW_HEIGHT_PX = 40
const REVIEW_GRID_CLASS =
  'grid grid-cols-[5rem_minmax(0,1fr)_minmax(0,1fr)]'

type ShipmentSkuMatchReviewProps = {
  items: ShipmentSkuReviewItem[]
}

export function ShipmentSkuMatchReview({ items }: ShipmentSkuMatchReviewProps) {
  const { t } = useTranslation('packagingSuppliers')
  const counts = useMemo(() => shipmentSkuReviewCounts(items), [items])
  const noneMatched = counts.matched === 0
  const [showAll, setShowAll] = useState(() =>
    defaultShipmentSkuReviewShowsAll(items)
  )
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null
  )

  useEffect(() => {
    setShowAll(defaultShipmentSkuReviewShowsAll(items))
  }, [items])

  const visibleItems = useMemo(
    () => visibleShipmentSkuReviewItems(items, showAll),
    [items, showAll]
  )

  const virtualizer = useVirtualizer({
    count: visibleItems.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => REVIEW_ROW_HEIGHT_PX,
    overscan: 8,
  })

  useEffect(() => {
    scrollElement?.scrollTo({ top: 0 })
  }, [showAll, scrollElement])

  let toggleLabel = t('shipmentsPage.skuFile.showAllSkus')
  if (showAll) {
    toggleLabel = t('shipmentsPage.skuFile.showUnmatchedSkus')
  }

  return (
    <div className='flex h-[min(28rem,60vh)] flex-col gap-3'>
      <div className='flex shrink-0 items-start justify-between gap-3'>
        <div>
          <p className='text-sm font-medium'>
            {t('shipmentsPage.skuFile.reviewSummary', {
              matched: counts.matched,
              total: counts.total,
            })}
          </p>
          <p className='text-sm text-muted-foreground'>
            {t('shipmentsPage.skuFile.reviewNotInLexCount', {
              count: counts.notInLex,
            })}
          </p>
        </div>
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={() => {
            setShowAll((current) => !current)
          }}
        >
          {toggleLabel}
        </Button>
      </div>
      {noneMatched && (
        <p className='shrink-0 text-sm text-destructive' role='alert'>
          {t('shipmentsPage.skuReview.noMatches')}
        </p>
      )}
      <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border'>
        <div
          role='row'
          className={cn(
            REVIEW_GRID_CLASS,
            'shrink-0 border-b bg-background text-sm font-medium'
          )}
        >
          <div role='columnheader' className='px-2 py-2 whitespace-nowrap'>
            {t('shipmentsPage.skuFile.reviewStatusColumn')}
          </div>
          <div role='columnheader' className='min-w-0 truncate px-2 py-2'>
            {t('shipmentsPage.skuFile.reviewSkuColumn')}
          </div>
          <div role='columnheader' className='min-w-0 truncate px-2 py-2'>
            {t('shipmentsPage.skuFile.reviewStatusLabel')}
          </div>
        </div>
        <div ref={setScrollElement} className='min-h-0 flex-1 overflow-auto'>
          <div
            role='rowgroup'
            className='relative w-full'
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const item = visibleItems[virtualRow.index]
              if (item == null) {
                return null
              }
              const rowInvalid = !item.matched
              let statusLabel = t('shipmentsPage.skuReview.notInLex')
              if (item.matched) {
                statusLabel = t('shipmentsPage.skuFile.reviewStatusMatched')
              }
              return (
                <div
                  key={item.sku}
                  role='row'
                  data-index={virtualRow.index}
                    className={cn(
                      REVIEW_GRID_CLASS,
                      'absolute top-0 left-0 w-full items-center text-sm',
                      rowInvalid && 'bg-destructive/10'
                    )}
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <div role='cell' className='px-2'>
                    <span className='inline-flex size-7 items-center justify-center'>
                      {item.matched && (
                        <Check
                          className='size-4 shrink-0 text-green-600 dark:text-green-500'
                          aria-hidden
                        />
                      )}
                      {rowInvalid && (
                        <X
                          className='size-4 shrink-0 text-destructive'
                          aria-hidden
                        />
                      )}
                    </span>
                  </div>
                  <div role='cell' className='truncate px-2'>
                    {item.sku}
                  </div>
                  <div role='cell' className='truncate px-2'>
                    {statusLabel}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
